import {
  getMediaFieldBySemantic,
  getMediaFieldValue,
  roundHalfEven,
  setMediaFieldValue,
} from "./utils"

import { BillingType } from "routes/Base/Admin/MarketSettings/List/BillingTypes/types"
import { ChangedPlanRecord } from "../../types"
import { MediaFields } from "./types"
import { calculationContext } from "./calculations"

export function calculateMarkups(li: ChangedPlanRecord) {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const planningFields = li[
    "planningMediaFields"
  ] as ChangedPlanRecord[typeof cc.fieldsKey]

  const buyingFields = li[
    "buyingMediaFields"
  ] as ChangedPlanRecord[typeof cc.fieldsKey]

  if (!planningFields || !buyingFields) return li

  const billingTypeField = getMediaFieldBySemantic("billingType")

  const billingType = getMediaFieldValue<BillingType["semanticCode"]>(
    fields,
    billingTypeField,
  )

  const withNetCostMarkup = calculateAndAssignNetCostMarkup(
    fields,
    planningFields,
    buyingFields,
    billingType,
  )

  const withPassthroughCostsMarkup = calculateAndAssignPassthroughCostsMarkup(
    withNetCostMarkup,
    planningFields,
    buyingFields,
  )

  return { ...li, [cc.fieldsKey]: withPassthroughCostsMarkup }
}

function calculateAndAssignNetCostMarkup(
  fields: MediaFields,
  planningFields: MediaFields,
  buyingFields: MediaFields,
  billingType?: BillingType["semanticCode"],
) {
  const netCostField = getMediaFieldBySemantic("netCost")
  const netCostMarkup = getMediaFieldBySemantic("netCostMarkup")

  if (!netCostField || !netCostMarkup) return fields

  const planningNetCost = getMediaFieldValue<number>(
    planningFields,
    netCostField,
  )
  const buyingNetCost = getMediaFieldValue<number>(buyingFields, netCostField)

  if (!planningNetCost || !buyingNetCost) return fields

  const isContra =
    !!billingType && ["ContraAc", "ContraNoAc"].includes(billingType)

  const netCostMarkupValue = isContra
    ? 0
    : roundHalfEven(planningNetCost - buyingNetCost)

  const withNetCostMarkup = setMediaFieldValue(
    netCostMarkupValue,
    fields,
    netCostMarkup,
  )

  return withNetCostMarkup
}

function calculateAndAssignPassthroughCostsMarkup(
  fields: MediaFields,
  planningFields: MediaFields,
  buyingFields: MediaFields,
) {
  const passthroughCostsField = getMediaFieldBySemantic("passthroughCosts")
  const passthroughCostsMarkup = getMediaFieldBySemantic(
    "passthroughCostsMarkup",
  )

  if (!passthroughCostsField || !passthroughCostsMarkup) return fields

  const planningPassthroughCosts = getMediaFieldValue<{ value?: number }>(
    planningFields,
    passthroughCostsField,
  )

  const buyingPassthroughCosts = getMediaFieldValue<{ value: number }>(
    buyingFields,
    passthroughCostsField,
  )

  if (!planningPassthroughCosts || !buyingPassthroughCosts) return fields

  const passthroughCostsMarkupValue = roundHalfEven(
    (planningPassthroughCosts?.value ?? 0) -
      (buyingPassthroughCosts?.value ?? 0),
  )

  const withPassthroughCostsMarkup = setMediaFieldValue(
    passthroughCostsMarkupValue,
    fields,
    passthroughCostsMarkup,
  )

  return withPassthroughCostsMarkup
}
